import React, { useState, useEffect } from 'react';
import {
    Form,
    Input,
    Button,
    Row,
    Col,
    Select,
    DatePicker,
    Space,
    Divider,
    Typography,
    InputNumber,
    Table,
    notification
} from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    ShoppingCartOutlined,
    CalendarOutlined,
    FileTextOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { purchaseService } from '../../services/purchaseService';
import { vendorService } from '../../services/vendorService';
import { itemService } from '../../services/itemService';
import { numberToWords } from '../../utils/numberToWords';

const { Option } = Select;
const { Title, Text } = Typography;
const { TextArea } = Input;

const PurchaseForm = ({ purchase, onSuccess, onCancel }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [vendors, setVendors] = useState([]);
    const [items, setItems] = useState([]);

    useEffect(() => {
        fetchVendors();
        fetchItems();
    }, []);

    useEffect(() => {
        if (purchase) {
            form.setFieldsValue({
                ...purchase,
                invoiceDate: dayjs(purchase.invoiceDate),
                supplierId: purchase.supplierId?.toString(),
                items: purchase.items?.map(item => ({
                    ...item,
                    itemId: item.itemId.toString()
                })),
                totalTax: purchase.totalTax,
                totalBeforeTax: purchase.totalBeforeTax,
                totalAfterTax: purchase.totalAfterTax
            });
        } else {
            form.resetFields();
            form.setFieldsValue({
                invoiceDate: dayjs(),
                totalTax: 0,
                items: [{}]
            });
        }
    }, [purchase, form]);

    const fetchVendors = async () => {
        try {
            const response = await vendorService.getAllVendors({ limit: 100 });
            setVendors(response.data || []);
        } catch (error) {
            console.error('Failed to fetch vendors', error);
        }
    };

    const fetchItems = async () => {
        try {
            const response = await itemService.getAllItems();
            setItems(response.data || []);
        } catch (error) {
            console.error('Failed to fetch items', error);
        }
    };

    const calculateTotal = (changedValues, allValues) => {
        let totalBeforeTax = 0;

        // Calculate totalBeforeTax from items
        if (allValues.items) {
            allValues.items.forEach(item => {
                const amount = (item.quantity || 0) * (item.rate || 0);
                totalBeforeTax += amount;
            });
        }

        // Get totalTax (user input or existing)
        const totalTax = parseFloat(allValues.totalTax || 0);

        // Calculate totalAfterTax
        const totalAfterTax = totalBeforeTax + totalTax;

        const totalAfterTaxValue = parseFloat(totalAfterTax.toFixed(2));

        // If items changed, update totalBeforeTax and totalAfterTax
        // If totalTax changed (handled by onValuesChange), just update totalAfterTax
        // We set proper fields based on what happened, but setting all is safe/easiest
        form.setFieldsValue({
            totalBeforeTax: totalBeforeTax.toFixed(2),
            totalAfterTax: totalAfterTaxValue,
            amountInWords: numberToWords(totalAfterTaxValue)
        });
    };

    const handleSubmit = async (values) => {
        try {
            setLoading(true);
            const data = {
                supplierId: parseInt(values.supplierId),
                invoiceDate: values.invoiceDate.toISOString(),
                invoiceNumber: values.invoiceNumber,
                // Send explicit totals
                totalBeforeTax: parseFloat(values.totalBeforeTax || 0),
                totalTax: parseFloat(values.totalTax || 0),
                totalAfterTax: parseFloat(values.totalAfterTax || 0),
                // Calculate effective GST rate for backend compatibility/storage
                gstRate: parseFloat(values.totalBeforeTax) > 0
                    ? (parseFloat(values.totalTax || 0) / parseFloat(values.totalBeforeTax)) * 100
                    : 0,
                ewayBillNo: values.ewayBillNo,
                cellGstInvoice: values.cellGstInvoice,
                amountInWords: values.amountInWords,
                items: values.items.map(item => ({
                    itemId: parseInt(item.itemId),
                    quantity: parseInt(item.quantity || 1),
                    rate: parseFloat(item.rate || 0)
                }))
            };

            if (purchase) {
                await purchaseService.updatePurchase(purchase.id, data);
                notification.success({
                    message: 'Success',
                    description: 'Purchase invoice updated successfully'
                });
            } else {
                await purchaseService.createPurchase(data);
                notification.success({
                    message: 'Success',
                    description: 'Purchase invoice created successfully'
                });
            }

            onSuccess();
        } catch (error) {
            notification.error({
                message: 'Error',
                description: error.response?.data?.error || error.message || 'Failed to save purchase invoice'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            onValuesChange={calculateTotal}
            style={{ padding: '0 10px' }}
        >
            <div style={{ marginBottom: 24 }}>
                <Space size="middle">
                    <ShoppingCartOutlined style={{ fontSize: 32, color: '#1890ff' }} />
                    <div>
                        <Title level={4} style={{ margin: 0 }}>
                            {purchase ? 'Edit Purchase Invoice' : 'Create Purchase Invoice'}
                        </Title>
                        <Text type="secondary">Record purchase from vendor</Text>
                    </div>
                </Space>
            </div>

            <Divider orientation="left"><Space><FileTextOutlined />Invoice Details</Space></Divider>
            <Row gutter={24}>
                <Col span={12}>
                    <Form.Item
                        label="Vendor"
                        name="supplierId"
                        rules={[{ required: true, message: 'Please select a vendor' }]}
                    >
                        <Select placeholder="Select Vendor" size="large" showSearch optionFilterProp="children">
                            {vendors.map(vendor => (
                                <Option key={vendor.id} value={vendor.id.toString()}>
                                    {vendor.vendorName}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        label="Invoice Date"
                        name="invoiceDate"
                        rules={[{ required: true, message: 'Please select invoice date' }]}
                    >
                        <DatePicker style={{ width: '100%' }} size="large" format="YYYY-MM-DD" />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={24}>
                <Col span={12}>
                    <Form.Item
                        label="Invoice Number"
                        name="invoiceNumber"
                        tooltip="Leave empty to auto-generate"
                    >
                        <Input prefix={<FileTextOutlined />} placeholder="Auto-generated if empty" size="large" />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={24}>
                <Col span={12}>
                    <Form.Item label="E-Way Bill No" name="ewayBillNo">
                        <Input placeholder="E-Way Bill Number" size="large" />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="Cell GST Invoice" name="cellGstInvoice">
                        <Input placeholder="Cell GST Invoice" size="large" />
                    </Form.Item>
                </Col>
            </Row>

            <Divider orientation="left"><Space><ShoppingCartOutlined />Items</Space></Divider>
            <Form.List name="items">
                {(fields, { add, remove }) => (
                    <>
                        {fields.map(({ key, name, ...restField }) => (
                            <Row key={key} gutter={16} align="middle" style={{ marginBottom: 16 }}>
                                <Col span={8}>
                                    <Form.Item
                                        {...restField}
                                        name={[name, 'itemId']}
                                        rules={[{ required: true, message: 'Select item' }]}
                                    >
                                        <Select placeholder="Select Item" showSearch optionFilterProp="children">
                                            {items.map(item => (
                                                <Option key={item.id} value={item.id.toString()}>
                                                    {item.itemName} ({item.itemType})
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={4}>
                                    <Form.Item
                                        {...restField}
                                        name={[name, 'quantity']}
                                        rules={[{ required: true, message: 'Qty' }]}
                                    >
                                        <InputNumber min={1} placeholder="Qty" style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item
                                        {...restField}
                                        name={[name, 'rate']}
                                        rules={[{ required: true, message: 'Rate' }]}
                                    >
                                        <InputNumber min={0} step={0.01} placeholder="Rate" style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={4}>
                                    <Form.Item>
                                        <Button
                                            type="text"
                                            danger
                                            icon={<DeleteOutlined />}
                                            onClick={() => remove(name)}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        ))}
                        <Form.Item>
                            <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                Add Item
                            </Button>
                        </Form.Item>
                    </>
                )}
            </Form.List>

            <Divider orientation="left">Totals</Divider>
            <Row gutter={24}>
                <Col span={8}>
                    <Form.Item label="Total Before Tax" name="totalBeforeTax">
                        <InputNumber
                            style={{ width: '100%' }}
                            prefix="₹"
                            readOnly
                            size="large"
                        />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="Total Tax" name="totalTax">
                        <InputNumber
                            style={{ width: '100%' }}
                            prefix="₹"
                            size="large"
                            placeholder="0.00"
                        />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="Total After Tax" name="totalAfterTax">
                        <InputNumber
                            style={{ width: '100%' }}
                            prefix="₹"
                            readOnly
                            size="large"
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Form.Item label="Amount in Words" name="amountInWords">
                <TextArea rows={2} placeholder="Enter amount in words" />
            </Form.Item>

            <Form.Item>
                <Space>
                    <Button type="primary" htmlType="submit" loading={loading} size="large">
                        {purchase ? 'Update' : 'Create'} Purchase Invoice
                    </Button>
                    <Button onClick={onCancel} size="large">
                        Cancel
                    </Button>
                </Space>
            </Form.Item>
        </Form>
    );
};

export default PurchaseForm;

